/** ROI model and pure geometry helpers (hit testing, membership, transforms). */
import type { Vec2 } from './types';

export type RoiKind = 'line' | 'rect' | 'ellipse' | 'polygon' | 'point';

interface RoiBase {
  id: string;
  kind: RoiKind;
  name: string;
  color: string;
  visible: boolean;
  locked: boolean;
}

export interface LineRoi extends RoiBase {
  kind: 'line';
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  /** Band width in pixels (integer >= 1). */
  width: number;
}

export interface RectRoi extends RoiBase {
  kind: 'rect';
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface EllipseRoi extends RoiBase {
  kind: 'ellipse';
  cx: number;
  cy: number;
  rx: number;
  ry: number;
}

export interface PolygonRoi extends RoiBase {
  kind: 'polygon';
  pts: Vec2[];
}

export interface PointRoi extends RoiBase {
  kind: 'point';
  x: number;
  y: number;
  /** Averaging window size in pixels (odd, >= 1). */
  size: number;
}

export type Roi = LineRoi | RectRoi | EllipseRoi | PolygonRoi | PointRoi;
export type AreaRoi = RectRoi | EllipseRoi | PolygonRoi;

export const ROI_COLORS = ['#ffcc00', '#00e5ff', '#ff4fd8', '#7CFC00', '#ff8c1a', '#b388ff', '#ff5252', '#40c4ff'];

/** ROIs with area statistics: rectangles, ellipses, polygons and points. */
export function hasAreaStats(r: Roi): r is AreaRoi | PointRoi {
  return r.kind !== 'line';
}

/** Validate untrusted ROI data (e.g. from a session file); returns only well-formed ROIs. */
export function sanitizeRois(input: unknown): { rois: Roi[]; dropped: number } {
  if (!Array.isArray(input)) return { rois: [], dropped: 0 };
  const fin = (...v: unknown[]) => v.every((x) => typeof x === 'number' && Number.isFinite(x));
  const out: Roi[] = [];
  for (const r of input as Record<string, unknown>[]) {
    if (!r || typeof r !== 'object' || typeof r.id !== 'string') continue;
    const base = {
      id: r.id,
      name: typeof r.name === 'string' ? r.name : 'ROI',
      color: typeof r.color === 'string' && /^#[0-9a-f]{6}$/i.test(r.color) ? r.color : '#ffcc00',
      visible: r.visible !== false,
      locked: r.locked === true,
    };
    switch (r.kind) {
      case 'line':
        if (fin(r.x0, r.y0, r.x1, r.y1, r.width) && (r.width as number) >= 1)
          out.push({
            ...base,
            kind: 'line',
            x0: r.x0 as number,
            y0: r.y0 as number,
            x1: r.x1 as number,
            y1: r.y1 as number,
            width: Math.round(r.width as number),
          });
        break;
      case 'rect':
        if (fin(r.x, r.y, r.w, r.h))
          out.push({ ...base, kind: 'rect', x: r.x as number, y: r.y as number, w: r.w as number, h: r.h as number });
        break;
      case 'ellipse':
        if (fin(r.cx, r.cy, r.rx, r.ry))
          out.push({ ...base, kind: 'ellipse', cx: r.cx as number, cy: r.cy as number, rx: r.rx as number, ry: r.ry as number });
        break;
      case 'point':
        if (fin(r.x, r.y, r.size) && (r.size as number) >= 1)
          out.push({ ...base, kind: 'point', x: r.x as number, y: r.y as number, size: Math.round(r.size as number) });
        break;
      case 'polygon': {
        const pts = Array.isArray(r.pts) ? (r.pts as { x: unknown; y: unknown }[]).filter((p) => p && fin(p.x, p.y)) : [];
        if (pts.length >= 3) out.push({ ...base, kind: 'polygon', pts: pts.map((p) => ({ x: p.x as number, y: p.y as number })) });
        break;
      }
    }
  }
  return { rois: out, dropped: input.length - out.length };
}

export function isArea(r: Roi): r is AreaRoi {
  return r.kind === 'rect' || r.kind === 'ellipse' || r.kind === 'polygon';
}

export function roiBounds(r: Roi): { x: number; y: number; w: number; h: number } {
  switch (r.kind) {
    case 'line': {
      const x = Math.min(r.x0, r.x1);
      const y = Math.min(r.y0, r.y1);
      return { x, y, w: Math.abs(r.x1 - r.x0), h: Math.abs(r.y1 - r.y0) };
    }
    case 'rect':
      return { x: Math.min(r.x, r.x + r.w), y: Math.min(r.y, r.y + r.h), w: Math.abs(r.w), h: Math.abs(r.h) };
    case 'ellipse':
      return { x: r.cx - Math.abs(r.rx), y: r.cy - Math.abs(r.ry), w: 2 * Math.abs(r.rx), h: 2 * Math.abs(r.ry) };
    case 'polygon': {
      let x0 = Infinity;
      let y0 = Infinity;
      let x1 = -Infinity;
      let y1 = -Infinity;
      for (const p of r.pts) {
        if (p.x < x0) x0 = p.x;
        if (p.y < y0) y0 = p.y;
        if (p.x > x1) x1 = p.x;
        if (p.y > y1) y1 = p.y;
      }
      return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
    }
    case 'point': {
      const h = r.size / 2;
      return { x: r.x - h, y: r.y - h, w: r.size, h: r.size };
    }
  }
}

export function pointInPolygon(x: number, y: number, pts: Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i];
    const b = pts[j];
    if (a.y > y !== b.y > y && x < ((b.x - a.x) * (y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

/** Membership test for a continuous point (use pixel centres i+0.5, j+0.5). */
export function containsPoint(r: AreaRoi | PointRoi, x: number, y: number): boolean {
  switch (r.kind) {
    case 'rect': {
      const b = roiBounds(r);
      return x >= b.x && x < b.x + b.w && y >= b.y && y < b.y + b.h;
    }
    case 'ellipse': {
      const dx = (x - r.cx) / r.rx;
      const dy = (y - r.cy) / r.ry;
      return dx * dx + dy * dy <= 1;
    }
    case 'polygon':
      return r.pts.length >= 3 && pointInPolygon(x, y, r.pts);
    case 'point': {
      const h = r.size / 2;
      return x >= r.x - h && x < r.x + h && y >= r.y - h && y < r.y + h;
    }
  }
}

/**
 * Inclusive range of pixel columns whose centres lie inside the ROI on row `py`,
 * or null. Polygons return their bounding columns (test each pixel with containsPoint).
 */
export function rowSpan(r: AreaRoi | PointRoi, py: number): [number, number] | null {
  const yc = py + 0.5;
  switch (r.kind) {
    case 'rect':
    case 'point': {
      const b = roiBounds(r);
      if (!(yc >= b.y && yc < b.y + b.h)) return null;
      const a = Math.ceil(b.x - 0.5);
      const e = Math.ceil(b.x + b.w - 0.5) - 1;
      return e >= a ? [a, e] : null;
    }
    case 'ellipse': {
      const dy = (yc - r.cy) / r.ry;
      if (!(dy * dy <= 1)) return null;
      const half = Math.abs(r.rx) * Math.sqrt(1 - dy * dy);
      const a = Math.ceil(r.cx - half - 0.5);
      const e = Math.floor(r.cx + half - 0.5);
      return e >= a ? [a, e] : null;
    }
    case 'polygon': {
      const b = roiBounds(r);
      if (!(yc >= b.y && yc <= b.y + b.h)) return null;
      return [Math.floor(b.x), Math.ceil(b.x + b.w)];
    }
  }
}

/** Iterate the indices of all image pixels whose centres lie inside the ROI. */
export function forEachPixel(r: AreaRoi | PointRoi, W: number, H: number, fn: (i: number, px: number, py: number) => void): void {
  const b = roiBounds(r);
  const x0 = Math.max(0, Math.floor(b.x));
  const y0 = Math.max(0, Math.floor(b.y));
  const x1 = Math.min(W - 1, Math.ceil(b.x + b.w));
  const y1 = Math.min(H - 1, Math.ceil(b.y + b.h));
  for (let py = y0; py <= y1; py++) for (let px = x0; px <= x1; px++) if (containsPoint(r, px + 0.5, py + 0.5)) fn(py * W + px, px, py);
}

export function polygonArea(pts: Vec2[]): number {
  let a = 0;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) a += (pts[j].x + pts[i].x) * (pts[j].y - pts[i].y);
  return Math.abs(a / 2);
}

export function distToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const L2 = dx * dx + dy * dy;
  let t = L2 ? ((px - ax) * dx + (py - ay) * dy) / L2 : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

/** Snap the end point of a line so its angle is a multiple of `stepDeg`. */
export function snapAngle(x0: number, y0: number, x1: number, y1: number, stepDeg = 15): Vec2 {
  const L = Math.hypot(x1 - x0, y1 - y0);
  const a = Math.atan2(y1 - y0, x1 - x0);
  const st = (stepDeg * Math.PI) / 180;
  const s = Math.round(a / st) * st;
  return { x: x0 + L * Math.cos(s), y: y0 + L * Math.sin(s) };
}

export function translateRoi<T extends Roi>(r: T, dx: number, dy: number): T {
  switch (r.kind) {
    case 'line':
      return { ...r, x0: r.x0 + dx, y0: r.y0 + dy, x1: r.x1 + dx, y1: r.y1 + dy };
    case 'rect':
      return { ...r, x: r.x + dx, y: r.y + dy };
    case 'ellipse':
      return { ...r, cx: r.cx + dx, cy: r.cy + dy };
    case 'polygon':
      return { ...r, pts: r.pts.map((p) => ({ x: p.x + dx, y: p.y + dy })) };
    case 'point':
      return { ...r, x: r.x + dx, y: r.y + dy };
  }
}

/** Normalise a rect so that w, h >= 0. */
export function normalizeRect(r: RectRoi): RectRoi {
  const b = roiBounds(r);
  return { ...r, x: b.x, y: b.y, w: b.w, h: b.h };
}

let idCounter = 0;
export function newRoiId(): string {
  idCounter++;
  return `roi-${Date.now().toString(36)}-${idCounter}`;
}
